import { Registration, RegistrationAffiliation, RegistrationPostSave, UserDto } from '@/api/dtos';
import delegateBanner from '@/assets/img/delegate-banner.png';
import Loading from '@/components/common/Loading';
import AccommodationSectionUpdate from '@/components/speaker/SpeakerAccommodationSection';
import SpeakerTravelSection from '@/components/speaker/SpeakerTravelSection';
import { AdminContext } from '@/contexts/AdminContext';
import { registrationUpdateText } from '@/enum/RegistrationUpdateText';
import { useFormSubmitValidationFailed } from '@/hooks/useFormSubmitValidationFailed';
import useSpeakerByGuid from '@/hooks/useSpeakerByGuid';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Paper, Typography, styled } from '@mui/material';
import { AuthenticatedUserContext, Form, FormSubmitButton, FormsFeatureContext, IValidationError, useFormLookups, useFormMetadata, useFormValidate } from '@ngt/forms';
import { IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import { immerable } from 'immer';
import { enqueueSnackbar } from 'notistack';
import { FC, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PackageProvider from '../registration/packages/PackageProvider';
import SessionsSectionUpdate from '../speaker/SpeakerPackages';
import RegistrationSection from './RegistrationSection';


export const StyledAccordianSummary = styled(AccordionSummary)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    borderRadius: "4px",

}))

export const StyledAccordion = styled(Accordion)(({theme}) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}))

export const StyledAccordionDetails = styled(AccordionDetails)(({theme}) => ({
    padding: theme.spacing(3),
}))


const StyledTypography = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(2),
}))

const BannerImage = styled('img')(({theme}) => ({
    width: '100%',
    maxHeight: '600px',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    marginBottom: '0px !important',
    paddingBottom: '0px !important'
}))

const ContentBox = styled(Box)(({theme}) => ({
    paddingLeft: '100px',
    paddingRight: '100px',
    paddingTop: '0px !important',
    marginTop: '0px !important',
    paddingBottom:'30px',

    [theme.breakpoints.down('md')]: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}));

const RegistrationUpdateForm: FC = () => {

    const navigate = useNavigate();

    const {serviceStackClient} = useContext(FormsFeatureContext);

    const params = useParams();

    const registrationGuid = params?.guid;

    const {registration, loading} = useSpeakerByGuid(registrationGuid);

    const initialFormValue = useMemo(() => ({
        [immerable]: true,
        ...registration,
        submittingCustomRegistration: true
    }), [registration]);

    const formMetadata = useFormMetadata(1);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });

    const validate: IFormValidate<Registration, IValidationError> = useFormValidate(formMetadata);

    const onSubmitValidationFailed = useFormSubmitValidationFailed();

    const { data: authUser } = useContext(AuthenticatedUserContext);
    const user = authUser as UserDto;
    
    useEffect(() => {
        if (registration && registration.isSpeaker){
                navigate('/speaker/' + registration.guid);
            }
    }, [registration, navigate]);

    const onSubmit:IFormSubmit<Registration, IValidationError> = async (formState) => {
        if(!formState.value)
        {
            return;
        }

        try {
            await serviceStackClient.post(new RegistrationPostSave({
                data: formState.value
            }))
            .then((_) => {
                enqueueSnackbar('Delegate saved successfully', {variant: 'success'});
                navigate('/');
            })
        }
        catch(err) {
            enqueueSnackbar('An error occurred while saving the registration. Please try again later.', {variant: 'error'});
        }
    }

    return (
        <Box
        >
        {/* this will be a picture banner */}

            <BannerImage
                src={delegateBanner}
                alt="speaker banner"
            />

            <ContentBox>

                <Paper 
                    elevation={2} 
                    sx={{
                        padding:'40px', 
                        pb: 10, 
                        margin: 0,
                        borderTopLeftRadius: "0px",
                        borderTopRightRadius: "0px"
                    }}>
                    {
                        loading && (
                            <>
                            <Loading/>
                            </>
                        )
                    }
                    {
                        (!loading && !registration) && (
                            <Alert severity="error" sx={{ width: '100%' }}>
                                <Typography>
                                    <b>No registration found</b> for this guid: <b>{registrationGuid}</b>. Please check the link you have received in your email.
                                </ Typography>
                            </Alert>
                        )
                    }
                    {
                        (registration && !loading) && (
                            <>
                                <Form
                                    initialValue={initialFormValue}
                                    debug={import.meta.env.DEV}
                                    lookups={lookups}
                                    onValidate={validate}
                                    validateOn={ValidateOn.onChange}
                                    onSubmit={onSubmit}
                                    onSubmitValidationFailed={onSubmitValidationFailed}
                                >
                                    <PackageProvider>

                                        {/* Standard delegate - RegistrationSection, and FormSubmitButton  */}
                                        {/* Custom Registration - RegistrationSection, SpeakerTravelSection, AccommodationSectionUpdate & SessionsSectionUpdate  */}
                                        <StyledTypography variant="body1" >
                                            <a href='https://www.breastcancertrials.org.au/' color="primary">Breast Cancer Trials (BCT)</a> is a world-leading clinical trials research organisation, dedicated to finding new and better treatments and prevention strategies for people affected by this disease. We conduct a multi-center national and international clinical trials research program, which involves more than 1,000 researchers across 118 institutions throughout Australia and New Zealand.
                                        </StyledTypography>
                                        <StyledTypography variant="body1">
                                            This year is the 47th year of the conference, which will be held in Brisbane, Queensland from Tuesday 28th - Friday 31st July, followed by a <a href='https://bct2026.org/?page_id=2495' color='primary'>Trainee and Early Career Day</a> on Saturday 1st August. <a href='https://bct2026.org/' color='primary'>Click here</a> to view the official website for our 2026 Annual Scientific Meeting (ASM).
                                        </StyledTypography>

                                            <StyledTypography variant="body1">
                                            { 
                                                registration.registrationAffiliation === RegistrationAffiliation.CAP ? registrationUpdateText.cap :
                                                registration.registrationAffiliation === RegistrationAffiliation.Board ? registrationUpdateText.board :
                                                registration.registrationAffiliation === RegistrationAffiliation.EducationTravelGrant ? registrationUpdateText.educationTravelGrant :
                                                registration.registrationAffiliation === RegistrationAffiliation.TraineeAndEarlyCareersDayGrant ? registrationUpdateText.traineeAndEarlyCareerTravelGrant :
                                                registration.registrationAffiliation === RegistrationAffiliation.Other ? registrationUpdateText.other :
                                                ''
                                            }
                                            </StyledTypography>
                                        <RegistrationSection/>

                                    
                                        {/* Custom Registration fields */}
                                        { ( registration.registrationAffiliation === RegistrationAffiliation.CAP  
                                                || registration.registrationAffiliation === RegistrationAffiliation.Board 
                                                ||  registration.registrationAffiliation === RegistrationAffiliation.GuestSpeaker
                                                ||  registration.registrationAffiliation === RegistrationAffiliation.TraineeAndEarlyCareersDayGrant
                                                ||  registration.registrationAffiliation === RegistrationAffiliation.EducationTravelGrant
                                                ||  registration.registrationAffiliation === RegistrationAffiliation.Other
                                            
                                            ) &&
                                            
                                            <>
                                                <AdminContext.Provider value={user?.isAdmin ?? false}>
                                                    <SpeakerTravelSection />
                                                </AdminContext.Provider>
                                                <AccommodationSectionUpdate />
                                            </>
                                        }

                                        <SessionsSectionUpdate />

                                        <FormSubmitButton 
                                            variant="contained"
                                            sx={{
                                                float: "right",
                                            }}
                                            >
                                            Submit
                                        </FormSubmitButton>         
                                    </PackageProvider>
                                </Form>

                            </>
                        )
                    }
                </Paper>
            </ContentBox>
        </Box>
    )
}

export default RegistrationUpdateForm;
